import { useQuery } from '@tanstack/react-query'
import type { ReactElement } from 'react'
import { useState } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { DrawdownPoint } from '@/domain/risk'
import { formatMinor } from '~/api/client'
import { ChartCard } from '~/charts/ChartCard'
import { fetchNetWorthSeries, isNoData } from '~/charts/data'
import { formatAxisDate, formatShareBp } from '~/charts/format'
import { chartCss } from '~/charts/theme'
import { Card, CardHead, StatCard } from '~/components/ui/Card'
import { Button, Filter } from '~/components/ui/Controls'
import { StatusPill } from '~/components/ui/Status'
import { DataTable } from '~/components/ui/Table'
import { PrivateHoldingsConcentrationSwitch } from '~/components/PrivateHoldings'
import { figureFormat, householdLocale } from '~/format/locale'
import { daysBetween } from '~/state/dates'
import type {
  ConcentrationSelection,
  DatedProvenance,
  DrawdownResponse,
  Measure,
  YearEndPin,
} from '~/state/queries'
import {
  useHoldsThroughCompany,
  useHouseholdShape,
  useConcentration,
  useDrawdown,
  usePinYearEnd,
  usePrivateHoldings,
  useYearEnds,
} from '~/state/queries'
import type { FiscalYearEnd } from '~/state/fiscal-year'
import {
  DEFAULT_FISCAL_YEAR_END,
  describeFiscalYearEnd,
  fiscalYearEndDate,
  fiscalYearLabel,
  useFiscalYearEnd,
} from '~/state/fiscal-year'
import { formatBasisPoints } from '~/state/private-holdings'
import type { FigureChoice } from '~/state/figures'
import { figuresFor, shownFigure, spendableAssumption } from '~/state/figures'
import { SETTINGS_PATH } from '~/navigation'

const RANGES: { days: number; label: string }[] = [
  { days: 90, label: '3 months' },
  { days: 365, label: '1 year' },
  { days: 3650, label: 'All' },
]

const TOP = 3
const DRAWDOWN_COLOR = 'var(--nw-serious)'
const TRADABLE_COLOR = 'var(--nw-series-1)'
const PRIVATE_COLOR = 'var(--nw-muted)'

/** A fall of a few basis points reads as a column of identical "0,0 %" at one decimal. */
const FINE_DRAWDOWN_BP = 100
const EXPORT_BASE: string = import.meta.env.VITE_API_BASE ?? '/api'

const PAGE_CSS = `
.nw-risk .nw-stats { display: flex; flex-wrap: wrap; gap: var(--nw-space-6); margin-bottom: var(--nw-stack); }
.nw-risk .nw-stats > * { flex: 1 1 220px; }
.nw-risk .nw-marks { display: inline-flex; flex-wrap: wrap; gap: var(--nw-space-1); margin-left: var(--nw-space-2); }
.nw-risk .nw-pins { align-items: center; display: flex; flex-wrap: wrap; gap: var(--nw-space-2); }
.nw-risk .nw-pins a { color: var(--nw-accent); font-size: var(--nw-text-cell); }
.nw-risk .nw-data { margin-bottom: 12px; }
.nw-risk .nw-section { font-size: var(--nw-text-page); letter-spacing: var(--nw-track-page); margin: var(--nw-stack) 0 var(--nw-space-3); }
`

const EXPOSURE_COLUMNS = [
  { id: 'exposure', label: 'Exposure' },
  { id: 'share', label: 'Share', width: '38%' },
  { id: 'value', label: 'Value', width: '190px', align: 'right' as const },
]

const DRAWDOWN_COLUMNS = [
  { id: 'date', label: 'Date', width: '130px' },
  { id: 'value', label: 'Value', width: '160px', align: 'right' as const },
  { id: 'high', label: 'High', width: '160px', align: 'right' as const },
  { id: 'short', label: 'Short by', width: '150px', align: 'right' as const },
  { id: 'below', label: 'Below', width: '110px', align: 'right' as const },
  { id: 'data', label: 'Data' },
]

/**
 * A pinned year as its columns, with Spendable among them only where it is a
 * second answer rather than Look-through spelled differently.
 *
 * The same rule the figure chips above are filtered by, applied to a table that
 * is archival: a household that has since disposed of its company still has
 * years where the two genuinely differ, and hiding the column on what is true
 * today would take a filed figure off the page.
 */
function pinColumns(showsSpendable: boolean): { id: string; label: string; width?: string; align?: 'right' }[] {
  return [
    { id: 'year', label: 'Year', width: '210px' },
    // The day the reading was taken on. On the page rather than in the export
    // alone, because it is no longer 31 December for every household and a
    // record filed under a year that does not name its own closing day is one
    // nobody can check.
    { id: 'closed', label: 'Closed', width: '130px' },
    { id: 'look', label: 'Look-through', width: '160px', align: 'right' as const },
    ...(showsSpendable
      ? [{ id: 'spend', label: 'Spendable', width: '160px', align: 'right' as const }]
      : []),
    { id: 'liquid', label: 'Liquid', width: '150px', align: 'right' as const },
    { id: 'under', label: 'Valued under', width: '260px' },
    { id: 'read', label: 'Read' },
  ]
}

function drawdownFormatter(maxDrawdownBp: number): (bp: number) => string {
  const digits = maxDrawdownBp < FINE_DRAWDOWN_BP ? 2 : 1
  const percent = figureFormat(householdLocale(), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
  return (bp) => `${percent.format(bp / 100)} %`
}

export function Risk(): ReactElement {
  const { settings } = usePrivateHoldings()
  const [figure, setFigure] = useState<Measure>('lookThrough')
  // Resolved here rather than beside the control: the query below and the chips
  // have to agree about which figure is being read, and a child that decided it
  // for itself would leave the two free to differ.
  const figures = figuresFor(useHouseholdShape(settings) ?? { company: false, unsellable: false })
  const shown = shownFigure(figures, figure)
  const [days, setDays] = useState(3650)
  const [view, setView] = useState<'chart' | 'table'>('chart')

  const concentration = useConcentration(settings)
  const drawdown = useDrawdown(settings, shown, days)

  return (
    <main className="nw nw-risk">
      <style>{chartCss + PAGE_CSS}</style>
      <h1>Risk</h1>
      {/* The sentence that was here described two of the three cards below; it
          is on them now, where each half is next to what it is about. */}
      {concentration.data && (
        <p className="nw-sub">
          {`${concentration.data.view.entries.length} exposures · on ${concentration.data.date} · all figures in ${concentration.data.baseCurrency}`}
        </p>
      )}

      <Concentration
        selection={concentration.data}
        isPending={concentration.isPending}
        error={concentration.error}
        countedInHeadline={settings.includeInLookThrough}
        haircutBp={settings.haircutBp}
      />

      <Drawdown
        result={drawdown.data}
        isPending={drawdown.isPending}
        isFetching={drawdown.isFetching}
        error={drawdown.error}
        figure={shown}
        figures={figures}
        onFigureChange={setFigure}
        days={days}
        onDaysChange={setDays}
        view={view}
        onViewChange={setView}
      />

      <YearEnds />
    </main>
  )
}

function Concentration({
  selection,
  isPending,
  error,
  countedInHeadline,
  haircutBp,
}: {
  selection: ConcentrationSelection | undefined
  isPending: boolean
  error: Error | null
  countedInHeadline: boolean
  haircutBp: number
}): ReactElement {
  const view = selection?.view
  const topShareBp = (view?.entries ?? [])
    .slice(0, TOP)
    .reduce((total, entry) => total + entry.shareBp, 0)

  const shown = (view?.entries ?? []).flatMap((entry) => {
    const origin = selection?.origins.get(entry.key)
    return origin ? [origin] : []
  })
  const projectedMinor = shown.reduce((total, origin) => total + origin.projectedMinor, 0)
  const carried = shown.filter((origin) => origin.stale)
  const staleSince = carried.reduce<string | null>(
    (soonest, origin) => earliest(soonest, origin.staleSince),
    null,
  )

  return (
    <Card>
      <CardHead
        id="nw-concentration-heading"
        title="Concentration"
        lede={`What one holding could do to the total. Per instrument, not per account: the same share held at two brokers is one exposure.${selection?.date ? ` Held on ${selection.date}.` : ''}`}
        aside={<PrivateHoldingsConcentrationSwitch />}
      />

      {isPending && <p className="nw-empty">Loading…</p>}

      {error && (
        <p className={isNoData(error) ? 'nw-empty' : 'nw-flag nw-flag--missing'}>
          {isNoData(error)
            ? 'No source reported on that date, so there is nothing to concentrate. A date that genuinely ended at zero would still be answered.'
            : `Could not load the exposures: ${error.message}`}
        </p>
      )}

      {selection && view && (
        <>
          <div className="nw-stats">
            <StatCard label="Largest position" note={view.largest?.label ?? 'Nothing priced'}>
              {view.largest ? formatShareBp(view.largest.shareBp) : '—'}
            </StatCard>
            <StatCard
              label={`Top ${TOP}`}
              note={`Of ${formatMinor(view.totalMinor, selection.baseCurrency)} counted here.`}
            >
              {formatShareBp(topShareBp)}
            </StatCard>
            <StatCard
              label="Herfindahl"
              note="The largest share alone cannot tell one holding at 30% from three at 30%."
            >
              {formatShareBp(view.hhiBp)}
            </StatCard>
          </div>

          <p className="nw-note">
            {concentrationNote(selection, countedInHeadline, haircutBp)}
          </p>

          <DataTable
            columns={EXPOSURE_COLUMNS}
            label={`Exposures by share of ${formatMinor(view.totalMinor, selection.baseCurrency)}`}
          >
            {view.entries.map((entry) => {
              const origin = selection.origins.get(entry.key)
              const flagged = origin !== undefined && (origin.projected || origin.stale)
              return (
                <tr key={entry.key} data-flagged={flagged}>
                  <th scope="row">
                    {entry.label}
                    {(entry.isPrivate || origin?.projected || origin?.stale) && (
                      <span className="nw-marks">
                        {entry.isPrivate && <StatusPill tone="manual" label="private" />}
                        {origin?.projected && (
                          <StatusPill tone="caution" label="Projected" />
                        )}
                        {origin?.stale && (
                          <StatusPill
                            tone="failed"
                            label={`Stale${origin.staleSince ? ` since ${origin.staleSince}` : ''}`}
                          />
                        )}
                      </span>
                    )}
                  </th>
                  <td>
                    <span className="nw-share">
                      <span className="nw-bar">
                        <i
                          style={{
                            background: entry.isPrivate ? PRIVATE_COLOR : TRADABLE_COLOR,
                            width: `${entry.shareBp / 100}%`,
                          }}
                        />
                      </span>
                      <b>{formatShareBp(entry.shareBp)}</b>
                    </span>
                  </td>
                  <td className="nw-num">
                    {formatMinor(entry.valueMinor, selection.baseCurrency)}
                  </td>
                </tr>
              )
            })}
          </DataTable>

          {projectedMinor > 0 && (
            <p className="nw-flag nw-flag--modelled">
              <span className="nw-flag-icon" aria-hidden="true">
                ◆
              </span>
              <span>
                <b>Modelled</b> — {formatMinor(projectedMinor, selection.baseCurrency)} of the
                exposures above rests on positions no source confirmed, so every share is measured
                against a total that was partly inferred.
              </span>
            </p>
          )}

          {carried.length > 0 && (
            <p className="nw-flag nw-flag--stale">
              <span className="nw-flag-icon" aria-hidden="true">
                ▲
              </span>
              <span>
                <b>Carried forward</b> — nothing has reported for some of these exposures since{' '}
                {staleSince ?? 'an unknown date'}; their last known positions are repeated rather
                than counted as a loss.
              </span>
            </p>
          )}

          {selection.unpriced.length > 0 && (
            <p className="nw-flag nw-flag--missing">
              <span className="nw-flag-icon" aria-hidden="true">
                ✕
              </span>
              <span>
                No price for {selection.unpriced.join(', ')}, so those holdings are missing from
                every share above rather than counted as zero.
              </span>
            </p>
          )}
        </>
      )}
    </Card>
  )
}

interface UnderwaterPoint extends DrawdownPoint {
  underwaterBp: number
}

function Drawdown({
  result,
  isPending,
  isFetching,
  error,
  figure,
  figures,
  onFigureChange,
  days,
  onDaysChange,
  view,
  onViewChange,
}: {
  result: DrawdownResponse | undefined
  isPending: boolean
  isFetching: boolean
  error: Error | null
  figure: Measure
  figures: readonly FigureChoice[]
  onFigureChange: (figure: Measure) => void
  days: number
  onDaysChange: (days: number) => void
  view: 'chart' | 'table'
  onViewChange: (view: 'chart' | 'table') => void
}): ReactElement {
  const points: UnderwaterPoint[] = (result?.points ?? []).map((point) => ({
    ...point,
    underwaterBp: -point.drawdownBp,
  }))
  const asPercent = drawdownFormatter(result?.maxDrawdownBp ?? 0)
  const assumption = spendableAssumption(figure)

  const dayFlags = result?.provenance ?? []
  const projectedDays = dayFlags.filter((day) => day.projected).length
  const staleDays = dayFlags.filter((day) => day.stale).length
  const staleSince = dayFlags
    .filter((day) => day.stale)
    .reduce<string | null>((soonest, day) => earliest(soonest, day.staleSince), null)
  const byDate = new Map(dayFlags.map((day) => [day.date, day]))

  return (
    <>
      <h2 className="nw-section">Drawdown</h2>
      <p className="nw-note">How far the total is from its best day.</p>
      <div className="nw-filters">
        <Filter
          label="Figure"
          choices={figures.map((entry) => ({ id: entry.key, label: entry.label }))}
          value={figure}
          onChange={(id) => onFigureChange(id as Measure)}
        />
        <Filter
          label="Range"
          choices={RANGES.map((range) => ({ id: String(range.days), label: range.label }))}
          value={String(days)}
          onChange={(id) => onDaysChange(Number(id))}
        />
      </div>

      {assumption !== null && <p className="nw-note">{assumption}</p>}

      {isPending && <p className="nw-empty">Loading…</p>}

      {error && (
        <p className={isNoData(error) ? 'nw-empty' : 'nw-flag nw-flag--missing'}>
          {isNoData(error)
            ? 'No snapshot has been taken yet, so there is no high to fall from.'
            : `Could not load the drawdown: ${error.message}`}
        </p>
      )}

      {result && (
        <>
          <div className="nw-stats">
            <StatCard
              label="Below the high"
              note={`${formatMinor(result.currentShortfallMinor, result.baseCurrency)} under it on ${result.currentDate}.`}
            >
              {asPercent(result.currentDrawdownBp)}
            </StatCard>
            <StatCard
              label="All-time high"
              note={`${result.highDate} · ${result.daysSinceHigh} day${result.daysSinceHigh === 1 ? '' : 's'} ago.`}
            >
              {formatMinor(result.highMinor, result.baseCurrency)}
            </StatCard>
            <StatCard label="Worst fall" note={`Deepest on ${result.maxDrawdownDate}.`}>
              {asPercent(result.maxDrawdownBp)}
            </StatCard>
          </div>

          {projectedDays > 0 && (
            <p className="nw-flag nw-flag--modelled">
              <span className="nw-flag-icon" aria-hidden="true">
                ◆
              </span>
              <span>
                <b>Modelled</b> — {projectedDays} of the {dayFlags.length} days plotted rest on
                positions no source confirmed, so both the high and the fall from it are partly
                inferred.
              </span>
            </p>
          )}

          {staleDays > 0 && (
            <p className="nw-flag nw-flag--stale">
              <span className="nw-flag-icon" aria-hidden="true">
                ▲
              </span>
              <span>
                <b>Carried forward</b> — {staleDays} of the {dayFlags.length} days plotted repeat an
                earlier day's positions; nothing has reported since{' '}
                {staleSince ?? 'an unknown date'}.
              </span>
            </p>
          )}

          <ChartCard
            title="Distance from the high"
            description="Measured against the running high of this figure, so a fall is only ever
              read against a level the total actually reached."
            view={view}
            onViewChange={onViewChange}
            legend={[]}
          >
            {points.length === 0 ? (
              <p className="nw-empty">No day in this range has a value.</p>
            ) : view === 'chart' ? (
              <div className={isFetching ? 'nw-plot nw-plot--loading' : 'nw-plot'}>
                <UnderwaterChart
                  points={points}
                  baseCurrency={result.baseCurrency}
                  maxDrawdownBp={result.maxDrawdownBp}
                  asPercent={asPercent}
                />
              </div>
            ) : (
              <DrawdownTable
                points={points}
                baseCurrency={result.baseCurrency}
                asPercent={asPercent}
                provenance={byDate}
              />
            )}
          </ChartCard>

          {!result.underwater && (
            <p className="nw-note">
              This figure is at its high today, so there is nothing to recover.
            </p>
          )}
        </>
      )}
    </>
  )
}

function UnderwaterChart({
  points,
  baseCurrency,
  maxDrawdownBp,
  asPercent,
}: {
  points: readonly UnderwaterPoint[]
  baseCurrency: string
  maxDrawdownBp: number
  asPercent: (bp: number) => string
}): ReactElement {
  const first = points[0]
  const last = points[points.length - 1]
  const spanDays = first && last ? daysBetween(first.date, last.date) : 0

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart
        data={points as UnderwaterPoint[]}
        margin={{ top: 8, right: 34, bottom: 0, left: 8 }}
      >
        <CartesianGrid stroke="var(--nw-grid)" strokeWidth={1} vertical={false} />
        <XAxis
          dataKey="date"
          axisLine={{ stroke: 'var(--nw-axis)' }}
          tickLine={false}
          minTickGap={48}
          tick={{ fill: 'var(--nw-muted)', fontSize: 12 }}
          tickFormatter={(date: string) => formatAxisDate(date, spanDays)}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          width={72}
          domain={['dataMin', 0]}
          tick={{ fill: 'var(--nw-muted)', fontSize: 12 }}
          tickFormatter={asPercent}
        />
        <ReferenceLine
          y={-maxDrawdownBp}
          stroke="var(--nw-axis)"
          strokeDasharray="4 4"
          label={{
            value: `worst ${asPercent(maxDrawdownBp)}`,
            position: 'insideBottomRight',
            fill: 'var(--nw-muted)',
            fontSize: 12,
          }}
        />
        <Tooltip
          cursor={{ stroke: 'var(--nw-axis)', strokeWidth: 1 }}
          content={({ active, payload }) =>
            active ? (
              <DrawdownTooltip
                point={payload?.[0]?.payload as UnderwaterPoint | undefined}
                baseCurrency={baseCurrency}
                asPercent={asPercent}
              />
            ) : null
          }
        />
        <Area
          type="monotone"
          dataKey="underwaterBp"
          name="Below the high"
          stroke={DRAWDOWN_COLOR}
          strokeWidth={2}
          fill={DRAWDOWN_COLOR}
          fillOpacity={0.16}
          isAnimationActive={false}
          activeDot={{ r: 5, strokeWidth: 2, stroke: 'var(--nw-surface)' }}
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}

function DrawdownTooltip({
  point,
  baseCurrency,
  asPercent,
}: {
  point: UnderwaterPoint | undefined
  baseCurrency: string
  asPercent: (bp: number) => string
}): ReactElement | null {
  if (!point) return null

  return (
    <div className="nw-tooltip">
      <h3>{point.date}</h3>
      <dl>
        <div>
          <dt />
          <dd className="nw-tooltip-name">Below the high</dd>
          <dd className="nw-tooltip-value">{asPercent(point.drawdownBp)}</dd>
        </div>
        <div>
          <dt />
          <dd className="nw-tooltip-name">Value</dd>
          <dd className="nw-tooltip-value">{formatMinor(point.amountMinor, baseCurrency)}</dd>
        </div>
        <div>
          <dt />
          <dd className="nw-tooltip-name">High</dd>
          <dd className="nw-tooltip-value">{formatMinor(point.peakMinor, baseCurrency)}</dd>
        </div>
      </dl>
      <p className="nw-tooltip-flags">High set {point.peakDate}</p>
    </div>
  )
}

function DrawdownTable({
  points,
  baseCurrency,
  asPercent,
  provenance,
}: {
  points: readonly UnderwaterPoint[]
  baseCurrency: string
  asPercent: (bp: number) => string
  provenance: ReadonlyMap<string, DatedProvenance>
}): ReactElement {
  return (
    <div className="nw-table-wrap">
      <DataTable
        dense
        columns={DRAWDOWN_COLUMNS}
        label={`Distance from the running high, in ${baseCurrency}`}
      >
        {[...points].reverse().map((point) => {
          const day = provenance.get(point.date)
          const flagged = day !== undefined && (day.projected || day.stale)
          return (
            <tr key={point.date} data-flagged={flagged}>
              <td>{point.date}</td>
              <td className="nw-num">{formatMinor(point.amountMinor, baseCurrency)}</td>
              <td className="nw-num">{formatMinor(point.peakMinor, baseCurrency)}</td>
              <td className="nw-num">{formatMinor(point.shortfallMinor, baseCurrency)}</td>
              <td className="nw-num">{asPercent(point.drawdownBp)}</td>
              <td>
                {day?.projected && 'Modelled'}
                {day?.projected && day.stale && ' · '}
                {day?.stale && 'Carried forward'}
                {!flagged && 'Fetched'}
              </td>
            </tr>
          )
        })}
      </DataTable>
    </div>
  )
}

/**
 * Two independent switches and a haircut decide what this card shows and what
 * the headline elsewhere shows. A sentence that names only one of them describes
 * a screen the reader is not looking at, and a share measured against a
 * discounted private valuation cannot say so by itself.
 */
function concentrationNote(
  selection: ConcentrationSelection,
  countedInHeadline: boolean,
  haircutBp: number,
): string {
  if (!selection.all.entries.some((entry) => entry.isPrivate)) {
    return 'Nothing privately valued was held on this date, so the switch above has nothing to add or take away.'
  }

  if (selection.includesPrivate) {
    const counted =
      haircutBp > 0
        ? `Private holdings are counted here after a ${formatBasisPoints(haircutBp)} haircut, not at the valuation itself`
        : 'Private holdings are counted here'
    return countedInHeadline
      ? `${counted}, so the largest position is the one nobody can sell and the figure says little about the rest.`
      : `${counted} although the headline leaves it out, so the largest position is the one nobody can sell and the figure says little about the rest.`
  }

  return countedInHeadline
    ? 'Private holdings are left out here, so this is the concentration of what could actually be traded; the headline still counts them.'
    : 'Private holdings are left out here and out of the headline, so neither figure counts what cannot be sold.'
}

function earliest(a: string | null, b: string | null): string | null {
  if (a === null) return b
  if (b === null) return a
  return a < b ? a : b
}

function YearEnds(): ReactElement {
  const pins = useYearEnds()
  const pin = usePinYearEnd()
  const yearEnd = useFiscalYearEnd()
  const { settings } = usePrivateHoldings()
  const holdsCompany = useHoldsThroughCompany(settings) ?? false
  const series = useQuery({
    queryKey: ['networth', 'series'],
    queryFn: fetchNetWorthSeries,
    retry: false,
  })

  const points = series.data?.points ?? []
  const first = points[0]?.date
  const last = points[points.length - 1]?.date
  const pinned = new Map((pins.data?.pins ?? []).map((entry) => [entry.year, entry]))
  const closing = yearEnd.data?.value ?? DEFAULT_FISCAL_YEAR_END
  // Held back until the closing day has been read rather than offered against
  // 31 December meanwhile: a button pressed in that moment would pin a day this
  // household never closes on, and the record would keep it.
  const years = first && last && yearEnd.data ? pinnableYears(first, last, closing) : []
  // The default export carries one reading per year so the file totals to the
  // filed answer; the superseded ones are only worth offering once there are any.
  const hasSuperseded = (pins.data?.pins ?? []).length > pinned.size
  const unreadable = pins.data?.unreadable ?? []
  // The server refuses a csv it cannot vouch for, so offering the link anyway
  // would hand back a json error in a file named like a tax record.
  const exportable = unreadable.length === 0

  return (
    <Card>
      <CardHead
        id="nw-year-ends-heading"
        title="Year ends"
        lede={
          <>
            A tax year here closes on {describeFiscalYearEnd(closing)}, and that day's figures are
            the ones a return is built from — so they are kept exactly as they were read, at the
            rates this household had configured then. A reprocessed year is appended beside the old
            reading, never over it. <a href={SETTINGS_PATH}>Settings</a> is where the closing day is
            changed; years already pinned keep the day they were read on.
          </>
        }
      />

      <div className="nw-pins">
        {years.map((year) => {
          // A tax year reaching back over 1 January is named for both, so the
          // button says what the household calls the year rather than the
          // integer it happens to be filed under.
          const label = fiscalYearLabel(fiscalYearEndDate(closing, year))
          return (
            <Button key={year} disabled={pin.isPending} onClick={() => pin.mutate(year)}>
              {pinned.has(year) ? `Re-read ${label}` : `Pin ${label}`}
            </Button>
          )
        })}
        {exportable && (pins.data?.pins.length ?? 0) > 0 && (
          <a href={`${EXPORT_BASE}/analytics/year-ends/export?format=csv&level=holdings`}>
            Export holdings as CSV
          </a>
        )}
        {exportable && hasSuperseded && (
          <a
            href={`${EXPORT_BASE}/analytics/year-ends/export?format=csv&level=holdings&revisions=all`}
          >
            Export every reading, superseded ones included
          </a>
        )}
      </div>

      {/*
        Withholding the years needs saying, or the card reads as a household
        with nothing to pin. The link is the way out of it: the day is set on
        the page it names, and that page gates on nothing.
      */}
      {yearEnd.error && (
        <p className="nw-flag nw-flag--missing">
          <span className="nw-flag-icon" aria-hidden="true">
            ✕
          </span>
          <span>
            Could not read which day this household's tax year closes —{' '}
            {yearEnd.error.message}. No year is offered until it is known, because a pin takes the
            day it was read on and keeps it. <a href={SETTINGS_PATH}>Settings</a> is where the
            closing day is set.
          </span>
        </p>
      )}

      {pin.error && (
        <p className="nw-flag nw-flag--missing">
          <span className="nw-flag-icon" aria-hidden="true">
            ✕
          </span>
          <span>
            {isNoData(pin.error)
              ? 'No source reported on the day that year closed, so there is no snapshot to pin. A year that genuinely ended at zero would still pin.'
              : `Could not pin that year end: ${pin.error.message}`}
          </span>
        </p>
      )}

      {unreadable.length > 0 && (
        <p className="nw-flag nw-flag--missing">
          <span className="nw-flag-icon" aria-hidden="true">
            ✕
          </span>
          <span>
            <b>
              {unreadable.length === 1
                ? 'One pinned year is on file and cannot be read'
                : `${unreadable.length} pinned years are on file and cannot be read`}
            </b>{' '}
            — {unreadable.map((entry) => entry.year ?? entry.key).join(', ')}. Re-reading the year
            writes a new revision beside the record rather than repairing it, so the stored value has
            to be fixed by hand. Until then the export is withheld: a csv missing a filed year reads
            exactly like a year nobody ever pinned.
            <br />
            {unreadable.map((entry) => (
              <small key={entry.key}>
                {entry.key}: {entry.reason}
                <br />
              </small>
            ))}
          </span>
        </p>
      )}

      {pins.data && pins.data.pins.length === 0 && unreadable.length === 0 && (
        <p className="nw-empty">No year end is pinned yet.</p>
      )}

      {pins.data && pins.data.pins.length > 0 && (
        <PinsTable pins={pins.data.pins} holdsCompany={holdsCompany} />
      )}
    </Card>
  )
}

function PinsTable({
  pins,
  holdsCompany,
}: {
  pins: readonly YearEndPin[]
  holdsCompany: boolean
}): ReactElement {
  // A year can hold several readings; only the last one is the figure in force.
  const inForce = new Map<number, number>()
  for (const pin of pins) inForce.set(pin.year, Math.max(inForce.get(pin.year) ?? 0, pin.revision))

  const showsSpendable =
    holdsCompany || pins.some((pin) => pin.spendableMinor !== pin.lookThroughMinor)

  // A pin is immune to recomputation, so a Spendable read while no rates were in
  // force stays a pre-tax figure for good, and the tax of nothing behind it reads
  // the same as a company that genuinely owed nothing.
  // Said only where the column it is about is on the page. It names Spendable
  // and the tax behind it, so a household that is shown neither would be told
  // its figures are missing something it is never offered.
  const untaxed = !showsSpendable
    ? []
    : [
        ...new Set(
          [...pins]
            .filter((pin) => !pin.taxRatesConfigured)
            .sort((a, b) => b.year - a.year)
            .map((pin) => fiscalYearLabel(pin.date)),
        ),
      ]

  return (
    <>
      <DataTable columns={pinColumns(showsSpendable)} label="Pinned year ends">
          {[...pins].reverse().map((entry) => (
            <tr
              key={`${entry.year}-${entry.revision}`}
              data-flagged={entry.stale || entry.projected}
            >
              <th scope="row">
                {fiscalYearLabel(entry.date)}
                <span className="nw-marks">
                  {inForce.get(entry.year) !== entry.revision && (
                    <StatusPill tone="manual" label="superseded" />
                  )}
                  {entry.projected && <StatusPill tone="caution" label="projected" />}
                  {entry.stale && <StatusPill tone="failed" label="stale" />}
                </span>
              </th>
              <td>{entry.date}</td>
              <td className="nw-num">{formatMinor(entry.lookThroughMinor, entry.baseCurrency)}</td>
              {showsSpendable && (
                <td className="nw-num">{formatMinor(entry.spendableMinor, entry.baseCurrency)}</td>
              )}
              <td className="nw-num">{formatMinor(entry.liquidMinor, entry.baseCurrency)}</td>
              <td>{describeAssumptions(entry)}</td>
              <td>
                {entry.pinnedAt.slice(0, 10)}
                {entry.revision > 0 && ` · revision ${entry.revision + 1}`}
              </td>
            </tr>
          ))}
      </DataTable>

      {untaxed.length > 0 && (
        <p className="nw-flag nw-flag--missing">
          <span className="nw-flag-icon" aria-hidden="true">
            ✕
          </span>
          <span>
            <b>No tax rates for those years</b> — nothing was subtracted for getting the company's
            money out when {listYears(untaxed)} {untaxed.length === 1 ? 'was' : 'were'} read, so the
            Spendable pinned there is the figure before tax rather than the honest one.{' '}
            <a href={SETTINGS_PATH}>Setting the rates in Settings</a>, from a date at or before the
            year concerned, leaves the pin as it was read; re-read the year for a figure that
            carries them.
          </span>
        </p>
      )}
    </>
  )
}

function listYears(years: readonly string[]): string {
  if (years.length < 2) return years[0] ?? ''
  return `${years.slice(0, -1).join(', ')} and ${years[years.length - 1]}`
}

/**
 * A pin is read years later, when nobody remembers where the switches were, and
 * an archival figure whose assumptions live only in the CSV is one nobody on
 * this page can check.
 */
function describeAssumptions(pin: YearEndPin): string {
  if (pin.includePrivate === null || pin.haircutBp === null) return 'Not recorded'
  const holding = pin.includePrivate ? 'Private holdings counted' : 'Private holdings left out'
  return pin.haircutBp > 0
    ? `${holding} · ${formatBasisPoints(pin.haircutBp)} haircut`
    : `${holding} · no haircut`
}

/** A closing day inside the history is pinnable; one before it has nothing to read. */
function pinnableYears(first: string, last: string, closing: FiscalYearEnd): number[] {
  const years: number[] = []
  for (let year = Number(first.slice(0, 4)); year <= Number(last.slice(0, 4)); year += 1) {
    const date = fiscalYearEndDate(closing, year)
    if (date >= first && date <= last) years.push(year)
  }
  return years.reverse()
}
